import React from 'react'
import { useAppDispatch } from '../redux'

import { DEBOUNCE_DELAY } from '.'
import { DEFAULT_EMBEDS } from '../../providers/UserAPIProvider'
import { searchUser } from '../../lib/api'
import { AddErrorAlert } from '../../slices/alert'

export const MIN_CHAR_COUNT = 2

const ID_DIFFS = (user, id) => user.id !== id
export const DEFAULT_FILTER = (res, _chars) => res
export const EXCLUDE_ME = id => (res, _chars) => {
  if (!res) {
    return []
  }
  return res.filter(user => ID_DIFFS(user, id()))
}

export const WITHIN_USERS = ids => (res, _chars) => {
  if (!res) {
    return []
  }

  return res.filter(user => ids.some(id => id === user.id))
}

const useDelayedUserSearch = (
  external,
  process = DEFAULT_FILTER,
  embeds = DEFAULT_EMBEDS,
  debounceDelay = DEBOUNCE_DELAY
) => {
  const dispatch = useAppDispatch()
  const [searchVal, _setSearchVal] = React.useState('')
  const cbRef = React.useRef(() => {})
  const timerRef = React.useRef(null)
  const processRef = React.useRef(DEFAULT_FILTER)
  const [results, setResults] = React.useState([])
  const [all, setAll] = React.useState([])
  const [error, setError] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [noResults, setNoResults] = React.useState(false)

  // Update process function
  React.useEffect(() => {
    if (typeof process === 'function') {
      processRef.current = process
    }
  }, [process])

  // Update search value and callback
  const setSearchVal = React.useCallback((val, cb) => {
    if (typeof val === 'string') {
      _setSearchVal(val)
    } else {
      _setSearchVal('')
    }

    if (typeof cb === 'function') {
      cbRef.current = cb
    } else {
      cbRef.current = () => {}
    }
  }, [])

  // Perform user lookup
  const handleSearch = React.useCallback(
    async chars => {
      let results = []

      try {
        setLoading(true)

        const all = await searchUser(chars, [], external, embeds)
        setAll(all)

        results = processRef.current(all, chars)
        setResults(results)
        setNoResults(results.length === 0)

        setLoading(false)
      } catch (err) {
        results = []

        console.error('Error with useDelayedSearch2', err)
        setError({ err })
        dispatch(AddErrorAlert({ err }))
      } finally {
        return results
      }
    },
    [external, embeds, dispatch]
  )

  // Perform search after delay
  React.useEffect(() => {
    if (searchVal.length <= MIN_CHAR_COUNT) {
      setAll([])
      setResults([])
      return
    }

    setLoading(true)
    timerRef.current = setTimeout(async () => {
      const res = await handleSearch(searchVal)
      cbRef.current(res)
    }, debounceDelay)

    return () => {
      clearTimeout(timerRef.current)
    }
  }, [searchVal, handleSearch, debounceDelay])

  return {
    search: setSearchVal,
    results,
    all,
    error,
    loading,
    noResults,
    setNoResults
  }
}

export default useDelayedUserSearch
